import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Camera,
  CameraOff,
  CheckCircle2,
  Keyboard,
  LogIn,
  LogOut,
  QrCode,
  ScanLine,
  ShieldAlert,
  SwitchCamera,
  XCircle,
} from "lucide-react";
import jsQR from "jsqr";
import { toast } from "sonner";
import {
  checkInVisitor,
  checkOutVisitor,
  scanOutcomeFromError,
  scanVisitorPass,
  type ScanAction,
  type ScanResult,
  type Visitor,
} from "../../api/visitors";
import {
  SCAN_RESULT_HINT,
  SCAN_RESULT_TONE,
  STATUS_BADGE,
  STATUS_LABEL,
  errMsg,
  fmtClock,
  fmtDate,
  fmtTimeOnly,
  initials,
} from "./visitorUi";

/**
 * Longest edge the jsQR path decodes at. A 1080p webcam frame is four times
 * the pixels of a 640px one for no extra reach — a QR held up to a camera is
 * plainly legible at this size, and the smaller buffer is what keeps the scan
 * loop off the main thread's back.
 */
const MAX_DECODE_WIDTH = 640;

type Decoder = (video: HTMLVideoElement) => Promise<string | null>;

/** Decode frames with jsQR — works on every browser and every OS. */
function makeCanvasDecoder(): Decoder {
  const canvas = document.createElement("canvas");
  // willReadFrequently keeps the surface on the CPU; without it every
  // getImageData stalls on a GPU readback, several times a second.
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  return async (video) => {
    if (!ctx) return null;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;
    const scale = Math.min(1, MAX_DECODE_WIDTH / vw);
    const w = Math.round(vw * scale);
    const h = Math.round(vh * scale);
    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(video, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    // attemptBoth (the default) also reads a light-on-dark code — a visitor
    // showing the pass from a dark-mode screenshot is common enough to pay
    // the second pass for.
    return jsQR(data, w, h, { inversionAttempts: "attemptBoth" })?.data ?? null;
  };
}

/**
 * The decoder for this scan session: the platform reader where it exists,
 * jsQR everywhere else.
 *
 * `BarcodeDetector` is an *operating system* capability, not a browser one —
 * Chrome ships it on Android, macOS and ChromeOS, and NOT on Windows or Linux.
 * So it is resolved per session as a fast lane, never as a gate: gating on it
 * left every Windows desk unable to scan, with the useless advice to "try
 * Chrome" when it already was Chrome.
 *
 * The native one can also be *present but broken* (it reports itself as
 * available, then every `detect` throws because the backing service is
 * missing), so the first failure demotes it to jsQR for good rather than
 * leaving the desk with a camera that silently never reads anything.
 */
function makeDecoder(): Decoder {
  const Native = typeof window !== "undefined" && (window as any).BarcodeDetector;

  // Built on first use, not up front: where the platform reader works this
  // allocates no canvas at all, and the canvas is only reached if the native
  // path is absent or turns out to be broken.
  let canvasDecoder: Decoder | null = null;
  const fallback: Decoder = (video) => {
    if (!canvasDecoder) canvasDecoder = makeCanvasDecoder();
    return canvasDecoder(video);
  };
  if (!Native) return fallback;

  let native: any;
  try {
    native = new Native({ formats: ["qr_code"] });
  } catch {
    return fallback;
  }
  let useNative = true;
  return async (video) => {
    if (useNative) {
      try {
        const found = await native.detect(video);
        return found?.[0]?.rawValue ?? null;
      } catch {
        useNative = false;
      }
    }
    return fallback(video);
  };
}

/**
 * Which way a camera points, for the label and for mirroring the preview.
 *
 * `facingMode` is the reliable answer but desktop browsers usually omit it,
 * so the device label is the fallback — a laptop webcam is front-facing even
 * when it never says so.
 */
function describeFacing(
  facingMode: string | undefined,
  label: string | undefined
): "front" | "back" | "unknown" {
  if (facingMode === "environment") return "back";
  if (facingMode === "user") return "front";
  const text = (label || "").toLowerCase();
  if (/back|rear|environment/.test(text)) return "back";
  if (/front|face|user|facetime|integrated|built-?in|webcam/.test(text)) return "front";
  return "unknown";
}

/** "Back camera" / "Camera 2" — never a bare hardware id. */
function cameraLabel(device: MediaDeviceInfo, index: number): string {
  if (device.label) return device.label;
  return `Camera ${index + 1}`;
}

type ScanState = {
  ok: boolean;
  result: ScanResult;
  message: string;
  visitor?: Visitor;
  /** Set on a VERIFY lookup: what a scan would do next. */
  nextAction?: "CHECK_IN" | "CHECK_OUT" | null;
  at: number;
};

const MODES: { key: ScanAction; label: string; hint: string }[] = [
  {
    key: "AUTO",
    label: "Auto",
    hint: "Checks the visitor in, or out if they are already inside.",
  },
  { key: "CHECK_IN", label: "Check In only", hint: "Refuses anything but an entry." },
  { key: "CHECK_OUT", label: "Check Out only", hint: "Refuses anything but an exit." },
  {
    key: "VERIFY",
    label: "Verify only",
    hint: "Shows who the pass belongs to without stamping anything.",
  },
];

/** Two short tones: rising for accepted, falling for refused. */
function beep(ok: boolean) {
  try {
    const Ctx =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = ok ? 880 : 300;
    gain.gain.value = 0.07;
    osc.start();
    osc.frequency.linearRampToValueAtTime(ok ? 1320 : 180, ctx.currentTime + 0.12);
    osc.stop(ctx.currentTime + 0.14);
    setTimeout(() => ctx.close().catch(() => {}), 400);
  } catch {
    // Audio is a nicety; a blocked AudioContext must never break a scan.
  }
}

/**
 * The reception / security desk.
 *
 * Camera scanning works on every browser and every OS: the platform's own
 * `BarcodeDetector` is used where it exists, and jsQR decodes the frames
 * everywhere else (see makeDecoder). The typed-code box stays as a permanent,
 * equal control rather than a fallback — a dead webcam, a visitor whose phone
 * is flat, a walk-in reading the code off a printed pass — and the backend
 * records those as MANUAL checks so the log always shows how a visitor got in.
 *
 * Every decision — duplicate, expired, cancelled, wrong tenant — is the
 * backend's (`utils/visitor_qr.py::plan_scan`). This screen only renders the
 * verdict, so there is one place where the rules live.
 */
export default function VisitorScanner({
  open,
  onClose,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  /** Fired after a successful check-in/out so the parent can refresh its lists. */
  onChanged: () => void;
}) {
  const [mode, setMode] = useState<ScanAction>("AUTO");
  const [cameraOn, setCameraOn] = useState(false);
  // Distinct from cameraOn: the gap between clicking and the permission
  // prompt being answered is seconds long, and a dead-looking black box in
  // that gap reads as "it is broken".
  const [starting, setStarting] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  // Populated once permission is granted — before that the browser hands back
  // unlabelled placeholder devices, which would make a useless picker.
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState("");
  const [facing, setFacing] = useState<"front" | "back" | "unknown">("unknown");
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<ScanState | null>(null);
  const [history, setHistory] = useState<ScanState[]>([]);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<any>(null);
  // Guards against the camera re-reading the same QR 3× a second while it is
  // still in frame: the same code is ignored for 4s after a scan, so a visitor
  // holding their phone up does not generate a stream of ALREADY_IN refusals.
  const recentRef = useRef<{ code: string; at: number } | null>(null);
  const busyRef = useRef(false);

  const submitCode = useCallback(
    async (code: string, source: "camera" | "manual") => {
      const trimmed = (code || "").trim();
      if (!trimmed || busyRef.current) return;

      const now = Date.now();
      if (
        source === "camera" &&
        recentRef.current &&
        recentRef.current.code === trimmed &&
        now - recentRef.current.at < 4000
      ) {
        return;
      }
      recentRef.current = { code: trimmed, at: now };

      busyRef.current = true;
      setBusy(true);
      try {
        const out = await scanVisitorPass(trimmed, mode);
        const state: ScanState = {
          ok: out.ok,
          result: out.result,
          message: out.message,
          visitor: out.visitor,
          nextAction: out.next_action ?? null,
          at: Date.now(),
        };
        setLast(state);
        setHistory((h) => [state, ...h].slice(0, 12));
        beep(out.ok);
        if (out.action) onChanged();
      } catch (e) {
        const refusal = scanOutcomeFromError(e);
        if (refusal) {
          const state: ScanState = {
            ok: false,
            result: refusal.result,
            message: refusal.message,
            visitor: refusal.visitor,
            at: Date.now(),
          };
          setLast(state);
          setHistory((h) => [state, ...h].slice(0, 12));
          beep(false);
        } else {
          toast.error(errMsg(e, "That scan could not be processed"));
        }
      } finally {
        busyRef.current = false;
        setBusy(false);
        if (source === "manual") setManual("");
      }
    },
    [mode, onChanged]
  );

  // ---- camera lifecycle ---------------------------------------------------

  const stopCamera = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
  }, []);

  /**
   * Open a camera and start decoding.
   *
   * `deviceId` picks a specific camera (the dropdown / Flip button);
   * otherwise the rear camera is *preferred* and the browser falls back on
   * its own. The constraint ladder matters: a laptop has no rear camera at
   * all, so asking for one as a hard requirement fails outright — this tries
   * the preference, then plain `video: true`, which is what makes a desktop
   * webcam work without the operator having to know why.
   */
  const startCamera = useCallback(
    async (deviceId?: string) => {
      setCameraError(null);
      if (!navigator.mediaDevices?.getUserMedia) {
        // Browsers hide the camera API entirely outside a secure context, so
        // over plain http://<lan-ip> a phone looks to the page exactly like a
        // machine with no camera. Blaming the device there sends whoever is
        // setting up a gate tablet hunting for a hardware fault that does not
        // exist — name the real cause instead.
        setCameraError(
          typeof window !== "undefined" && window.isSecureContext === false
            ? "The camera is blocked because this page is not on a secure connection. Open it over HTTPS (or on localhost) to scan, or use the code box below."
            : "No camera is available on this device. Use the code box below."
        );
        setStarting(false);
        return;
      }

      setStarting(true);
      // Anything already running has to be released first: a second
      // getUserMedia while the old track is live fails on some devices
      // (one-camera-at-a-time), which is exactly the Flip case.
      if (streamRef.current) stopCamera();

      const attempts: MediaStreamConstraints[] = deviceId
        ? [{ video: { deviceId: { exact: deviceId } }, audio: false }]
        : [
            { video: { facingMode: { ideal: "environment" } }, audio: false },
            { video: true, audio: false },
          ];

      let stream: MediaStream | null = null;
      let lastError: any = null;
      for (const constraints of attempts) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          break;
        } catch (e: any) {
          lastError = e;
          // A denial will not be fixed by trying a different camera.
          if (e?.name === "NotAllowedError" || e?.name === "SecurityError") break;
        }
      }

      if (!stream) {
        setStarting(false);
        const name = lastError?.name || "";
        setCameraError(
          name === "NotAllowedError" || name === "SecurityError"
            ? "Camera permission was denied. Allow it in the browser's site settings, or use the code box below."
            : name === "NotFoundError" || name === "DevicesNotFoundError"
              ? "No camera was found on this device. Use the code box below."
              : name === "NotReadableError"
                ? "The camera is already in use by another app. Close it and try again."
                : "The camera could not be started. Use the code box below."
        );
        return;
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // try/catch, not .catch(): play() rejects when autoplay is blocked but
        // can also THROW synchronously (and returns undefined on older
        // engines), either of which would take the whole scanner down.
        try {
          await videoRef.current.play();
        } catch {
          /* The stream is attached; the frames flow even if play() complained. */
        }
      }

      // Which camera actually opened, and what else is available. Labels are
      // only populated AFTER permission is granted, so enumerating here rather
      // than up-front is what makes the dropdown readable ("Back camera")
      // instead of a list of opaque ids.
      const track = stream.getVideoTracks()[0];
      const settings: any = track?.getSettings?.() || {};
      setActiveDeviceId(settings.deviceId || deviceId || "");
      setFacing(describeFacing(settings.facingMode, track?.label));
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        setCameras(all.filter((d) => d.kind === "videoinput"));
      } catch {
        setCameras([]);
      }

      const decode = makeDecoder();
      timerRef.current = setInterval(async () => {
        const video = videoRef.current;
        if (!video || video.readyState < 2 || busyRef.current) return;
        try {
          const raw = await decode(video);
          if (raw) submitCode(raw, "camera");
        } catch {
          // A single failed frame is normal (mid-resize, backgrounded tab).
        }
      }, 320);
      setCameraOn(true);
      setStarting(false);
    },
    [stopCamera, submitCode]
  );

  /** Next camera in the list — the Flip button on a phone. */
  const flipCamera = useCallback(() => {
    if (cameras.length < 2) return;
    const i = cameras.findIndex((c) => c.deviceId === activeDeviceId);
    const next = cameras[(i + 1) % cameras.length];
    startCamera(next.deviceId);
  }, [cameras, activeDeviceId, startCamera]);

  // The camera follows the dialog. Opening it IS the request to scan — making
  // the operator click "Start camera" afterwards is one pointless step at a
  // desk where the visitor is already standing there. Closing releases the
  // device: a stream left running keeps the camera light on behind the page.
  //
  // Deliberately keyed on `open` alone. Including startCamera/stopCamera would
  // re-run this on every camera switch (they are rebuilt when the device list
  // changes), tearing down the stream that was just opened.
  useEffect(() => {
    if (open) startCamera();
    else stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // …and on unmount, whatever the dialog state says.
  useEffect(() => () => stopCamera(), [stopCamera]);

  // ---- explicit action on a verified pass ---------------------------------

  const actOn = async (action: "CHECK_IN" | "CHECK_OUT") => {
    const visitorId = last?.visitor?.id;
    if (!visitorId || busyRef.current) return;
    // Goes through the manual desk endpoints rather than re-posting the code:
    // the scan response deliberately carries no credential (so a list or a
    // verify can never hand out a working pass), and those endpoints run the
    // SAME plan_scan validation — only the recorded method differs.
    busyRef.current = true;
    setBusy(true);
    try {
      const updated =
        action === "CHECK_IN"
          ? await checkInVisitor(visitorId)
          : await checkOutVisitor(visitorId);
      const state: ScanState = {
        ok: true,
        result: "OK",
        message: action === "CHECK_IN" ? "Checked in." : "Checked out.",
        visitor: updated,
        at: Date.now(),
      };
      setLast(state);
      setHistory((h) => [state, ...h].slice(0, 12));
      beep(true);
      onChanged();
    } catch (e) {
      const refusal = scanOutcomeFromError(e);
      toast.error(refusal?.message || errMsg(e, "That action was refused"));
      beep(false);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const tone = last ? SCAN_RESULT_TONE[last.result] : null;
  const toneClass =
    tone === "ok"
      ? "border-green-200 bg-green-50"
      : tone === "warn"
        ? "border-amber-200 bg-amber-50"
        : "border-red-200 bg-red-50";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine size={18} /> Scan a visitor pass
          </DialogTitle>
          <DialogDescription>
            Hold the visitor's QR pass up to the camera, or type the code
            printed under it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* --------------------------------------------------------- mode */}
          <div>
            <div className="flex flex-wrap items-center gap-1">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  onClick={() => setMode(m.key)}
                  title={m.hint}
                  className={`px-3 py-1.5 text-sm rounded-md border-b-2 transition-colors ${
                    mode === m.key
                      ? "border-emerald-600 text-emerald-700 font-semibold"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {MODES.find((m) => m.key === mode)?.hint}
            </p>
          </div>

          {/* ------------------------------------------------------- camera */}
          <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded-xl bg-gray-900">
            <video
              ref={videoRef}
              className={`h-full w-full object-cover ${cameraOn ? "" : "hidden"}`}
              // A front camera is mirrored so the operator's movements match
              // what they see. Display only: the decoder reads the video
              // element's own frames, which the CSS transform never touches,
              // so a mirrored preview still decodes.
              style={facing === "front" ? { transform: "scaleX(-1)" } : undefined}
              muted
              playsInline
            />
            {cameraOn && (
              <>
                {/* Reticle — shows the visitor where to hold the pass. */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="h-44 w-44 rounded-lg border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
                </div>
                {busy && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-3 py-1 text-xs text-white">
                    Checking…
                  </div>
                )}
              </>
            )}
            {!cameraOn && (
              <div className="px-6 py-8 text-center">
                <QrCode className="mx-auto mb-3 h-10 w-10 text-gray-500" />
                <p className="text-sm text-gray-300">
                  {starting
                    ? "Opening the camera…"
                    : "Point the camera at the visitor's QR pass."}
                </p>
                {!starting && !cameraError && (
                  <p className="mt-1 text-xs text-gray-400">
                    Uses the rear camera on a phone or tablet, and the built-in
                    webcam on a laptop.
                  </p>
                )}
                {cameraError && (
                  <p className="mx-auto mt-3 max-w-sm text-xs text-amber-300">
                    {cameraError}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ----------------------------------------------------- controls */}
          <div className="flex flex-wrap items-center gap-2">
            {cameraOn ? (
              <button className="gt-btn is-ghost is-sm" onClick={stopCamera}>
                <CameraOff size={14} /> Stop camera
              </button>
            ) : (
              <button
                className="gt-btn is-primary is-sm"
                disabled={starting}
                onClick={() => startCamera(activeDeviceId || undefined)}
              >
                <Camera size={14} /> {starting ? "Opening…" : "Start camera"}
              </button>
            )}

            {/* Flip is the phone gesture; the dropdown is for a desk with
                several cameras plugged in. Both appear only once there is
                actually something to switch to. */}
            {cameraOn && cameras.length > 1 && (
              <>
                <button className="gt-btn is-ghost is-sm" onClick={flipCamera}>
                  <SwitchCamera size={14} /> Flip
                </button>
                <select
                  className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700"
                  value={activeDeviceId}
                  onChange={(e) => startCamera(e.target.value)}
                  aria-label="Camera"
                >
                  {cameras.map((c, i) => (
                    <option key={c.deviceId || i} value={c.deviceId}>
                      {cameraLabel(c, i)}
                    </option>
                  ))}
                </select>
              </>
            )}

            {cameraOn && facing !== "unknown" && (
              <span className="text-xs text-gray-500">
                Using the {facing} camera
              </span>
            )}
          </div>

          {/* The typed-code path is a first-class control, not a fallback —
              see the component docstring. */}
          <div className="rounded-lg border border-gray-200 p-3">
            <Label className="flex items-center gap-1.5 text-xs">
              <Keyboard size={13} /> Or enter the pass code
            </Label>
            <form
              className="mt-2 flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                submitCode(manual, "manual");
              }}
            >
              <Input
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="VIS-7KQ4MX"
                className="font-mono tracking-wider"
                autoComplete="off"
              />
              <button
                type="submit"
                className="gt-btn is-primary shrink-0"
                disabled={busy || !manual.trim()}
              >
                {busy ? "Checking…" : "Submit"}
              </button>
            </form>
            <p className="mt-1.5 text-xs text-gray-500">
              A hand-held barcode scanner that types into this box works too.
            </p>
          </div>

          {/* --------------------------------------------------------- last */}
          {/* Only once something has been scanned: an empty "Scans will appear
              here" panel would push the camera off a laptop screen for no
              information at all. */}
          {last && (
            <div className={`rounded-xl border p-4 ${toneClass}`}>
              <div className="flex items-start gap-2">
                {last.ok ? (
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                ) : tone === "warn" ? (
                  <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                ) : (
                  <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900">{last.message}</div>
                  {SCAN_RESULT_HINT[last.result] && (
                    <div className="mt-1 text-xs text-gray-600">
                      {SCAN_RESULT_HINT[last.result]}
                    </div>
                  )}
                </div>
              </div>

              {last.visitor && (
                <div className="mt-4 border-t border-black/5 pt-3">
                  <div className="flex items-center gap-3">
                    {last.visitor.photo_url ? (
                      <img
                        src={last.visitor.photo_url}
                        alt=""
                        className="h-11 w-11 rounded-full bg-white object-cover"
                      />
                    ) : (
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-sm font-semibold text-gray-600">
                        {initials(last.visitor.visitor_name)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-900">
                        {last.visitor.visitor_name}
                        {last.visitor.visitor_count > 1 && (
                          <span className="ml-1 text-xs text-gray-500">
                            +{last.visitor.visitor_count - 1}
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-gray-600">
                        {last.visitor.company_name || "—"}
                      </div>
                    </div>
                    <Badge
                      className={`ml-auto shrink-0 rounded-md ${
                        STATUS_BADGE[last.visitor.status]
                      }`}
                    >
                      {STATUS_LABEL[last.visitor.status]}
                    </Badge>
                  </div>

                  <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">Host</dt>
                      <dd className="text-right text-gray-900">
                        {last.visitor.host_name || "—"}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">Purpose</dt>
                      <dd className="text-right text-gray-900">
                        {last.visitor.purpose || "—"}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">Scheduled</dt>
                      <dd className="text-right text-gray-900">
                        {fmtDate(last.visitor.visit_date)} ·{" "}
                        {fmtClock(last.visitor.expected_time)}
                      </dd>
                    </div>
                    {last.visitor.checked_in_at && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-gray-500">Entry</dt>
                        <dd className="text-right text-gray-900">
                          {fmtTimeOnly(last.visitor.checked_in_at)}
                        </dd>
                      </div>
                    )}
                    {last.visitor.checked_out_at && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-gray-500">Exit</dt>
                        <dd className="text-right text-gray-900">
                          {fmtTimeOnly(last.visitor.checked_out_at)}
                        </dd>
                      </div>
                    )}
                  </dl>
                  {last.visitor.notes && (
                    <div className="mt-2 text-xs text-gray-600">
                      {last.visitor.notes}
                    </div>
                  )}

                  {/* Verify mode looked the pass up without acting — offer the
                      step it says is next. */}
                  {mode === "VERIFY" && last.nextAction && (
                    <button
                      className="gt-btn is-primary is-sm mt-3 w-full justify-center"
                      disabled={busy}
                      onClick={() => actOn(last.nextAction!)}
                    >
                      {last.nextAction === "CHECK_IN" ? (
                        <>
                          <LogIn size={14} /> Check in now
                        </>
                      ) : (
                        <>
                          <LogOut size={14} /> Check out now
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Scanned several in a row — a tiny tally so the desk can see the
              queue it just cleared without leaving the dialog. */}
          {history.length > 1 && (
            <div className="text-xs text-gray-500">
              This session:{" "}
              {history.map((h, i) => (
                <span key={`${h.at}-${i}`}>
                  {i > 0 && ", "}
                  <span className={h.ok ? "text-gray-700" : "text-red-600"}>
                    {h.visitor?.visitor_name || "Unknown pass"}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
